import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const AVATAR_CACHE_KEY = 'cached_avatar_url';
const AVATAR_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

interface CachedAvatar {
  url: string;
  timestamp: number;
}

export const useCachedAvatar = () => {
  const { user } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setAvatarUrl(null);
      setLoading(false);
      return;
    }

    const userAvatarUrl = user.user_metadata?.avatar_url;
    
    if (!userAvatarUrl) {
      setAvatarUrl(null);
      setLoading(false);
      return;
    }

    // Try to get from cache first
    try {
      const cached = localStorage.getItem(`${AVATAR_CACHE_KEY}_${user.id}`);
      if (cached) {
        const parsedCache: CachedAvatar = JSON.parse(cached);
        const isExpired = Date.now() - parsedCache.timestamp > AVATAR_CACHE_TTL;
        
        if (!isExpired && parsedCache.url === userAvatarUrl) {
          setAvatarUrl(parsedCache.url);
          setLoading(false);
          return;
        }
      }
    } catch (error) {
      console.warn('Failed to parse cached avatar:', error);
    }

    // Test if the avatar URL is valid and cache it
    const img = new Image();
    img.onload = () => {
      setAvatarUrl(userAvatarUrl);
      setLoading(false);
      
      // Cache the successful URL
      try {
        const cacheData: CachedAvatar = {
          url: userAvatarUrl,
          timestamp: Date.now()
        };
        localStorage.setItem(`${AVATAR_CACHE_KEY}_${user.id}`, JSON.stringify(cacheData));
      } catch (error) {
        console.warn('Failed to cache avatar URL:', error);
      }
    };
    
    img.onerror = () => {
      setAvatarUrl(null);
      setLoading(false);
      
      // Remove invalid URL from cache
      try {
        localStorage.removeItem(`${AVATAR_CACHE_KEY}_${user.id}`);
      } catch (error) {
        console.warn('Failed to remove invalid avatar from cache:', error);
      }
    };
    
    img.src = userAvatarUrl;
  }, [user]);

  return { avatarUrl, loading };
};